/**
 * Browser-compatible STL parser.
 * Handles both binary and ASCII STL formats using DataView/ArrayBuffer.
 * Ported from backend stlToGlb.js (Node Buffer → browser DataView).
 */

/**
 * Parse a binary STL ArrayBuffer.
 * Format: 80-byte header, 4-byte uint32LE face count, then 50 bytes per face.
 */
function parseBinarySTL(buffer) {
  const view = new DataView(buffer)
  const faceCount = view.getUint32(80, true) // little-endian
  const positions = new Float32Array(faceCount * 9)
  const normals = new Float32Array(faceCount * 9)
  let offset = 84

  for (let i = 0; i < faceCount; i++) {
    const nx = view.getFloat32(offset, true); offset += 4
    const ny = view.getFloat32(offset, true); offset += 4
    const nz = view.getFloat32(offset, true); offset += 4

    for (let v = 0; v < 3; v++) {
      const idx = i * 9 + v * 3
      positions[idx]     = view.getFloat32(offset, true); offset += 4
      positions[idx + 1] = view.getFloat32(offset, true); offset += 4
      positions[idx + 2] = view.getFloat32(offset, true); offset += 4
      normals[idx]     = nx
      normals[idx + 1] = ny
      normals[idx + 2] = nz
    }
    offset += 2 // attribute byte count
  }

  return { positions, normals, faceCount }
}

/**
 * Parse an ASCII STL string.
 */
function parseAsciiSTL(text) {
  const faceRegex = /facet\s+normal\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s+outer\s+loop\s+vertex\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s+vertex\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s+vertex\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s+endloop\s+endfacet/gi

  const posArray = []
  const normArray = []
  let match
  while ((match = faceRegex.exec(text)) !== null) {
    const nx = parseFloat(match[1]), ny = parseFloat(match[2]), nz = parseFloat(match[3])
    for (let v = 0; v < 3; v++) {
      posArray.push(
        parseFloat(match[4 + v * 3]),
        parseFloat(match[5 + v * 3]),
        parseFloat(match[6 + v * 3]),
      )
      normArray.push(nx, ny, nz)
    }
  }

  return {
    positions: new Float32Array(posArray),
    normals: new Float32Array(normArray),
    faceCount: posArray.length / 9,
  }
}

/**
 * Auto-detect format and parse STL ArrayBuffer.
 */
export function parseSTL(arrayBuffer) {
  const view = new DataView(arrayBuffer)

  // Check if starts with "solid" (ASCII STL marker)
  const headerBytes = new Uint8Array(arrayBuffer, 0, Math.min(80, arrayBuffer.byteLength))
  const header = String.fromCharCode(...headerBytes).trim()

  if (header.startsWith('solid')) {
    // Cross-check: expected binary size vs actual
    const expectedFaceCount = view.getUint32(80, true)
    const expectedBinarySize = 84 + expectedFaceCount * 50
    if (expectedBinarySize !== arrayBuffer.byteLength) {
      // Size mismatch → likely ASCII
      const text = new TextDecoder().decode(arrayBuffer)
      return parseAsciiSTL(text)
    }
  }

  return parseBinarySTL(arrayBuffer)
}
